import { useEffect, useState } from "react";
import type { OutlineItem } from "../lib/protocol.ts";

/** Bookmarks (document outline) sidebar: jump, add from the current
 *  page, rename in place, re-nest, reorder, delete. Every edit writes
 *  the whole tree back through the worker (undoable, exported). */
export function BookmarksPanel({
  items,
  currentPage,
  pageCount,
  busy,
  onJump,
  onApply,
  onClose,
  paneWidth,
}: {
  /** The document's outline, owned by the parent (which re-reads it
   *  after every baseline change — including undo). Null = loading. */
  items: OutlineItem[] | null;
  currentPage: number;
  pageCount: number;
  busy: boolean;
  onJump: (page: number) => void;
  onApply: (items: OutlineItem[]) => void;
  onClose: () => void;
  paneWidth: number;
}) {
  const [renaming, setRenaming] = useState<number | null>(null);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && renaming === null) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, renaming]);

  /** Rows that nest UNDER row i (its subtree), for move/indent moves. */
  const subtreeLength = (list: OutlineItem[], i: number): number => {
    let n = 1;
    while (i + n < list.length && list[i + n]!.level > list[i]!.level) n += 1;
    return n;
  };

  const mutate = (next: OutlineItem[]) => onApply(next);

  const addHere = () => {
    const list = items ?? [];
    mutate([
      ...list,
      {
        title: `Page ${currentPage + 1}`,
        page: currentPage,
        level: 0,
        open: true,
      },
    ]);
    setRenaming(list.length);
    setDraft(`Page ${currentPage + 1}`);
  };

  const move = (i: number, dir: -1 | 1) => {
    const list = [...(items ?? [])];
    const len = subtreeLength(list, i);
    const block = list.splice(i, len);
    // Land before the previous sibling / after the next sibling's subtree.
    let target = i;
    if (dir === -1) {
      target = i - 1;
      while (target > 0 && list[target]!.level > block[0]!.level) target -= 1;
    } else {
      let j = i;
      if (j < list.length) {
        j += 1;
        while (j < list.length && list[j]!.level > block[0]!.level) j += 1;
      }
      target = j;
    }
    list.splice(Math.max(0, Math.min(target, list.length)), 0, ...block);
    mutate(list);
  };

  const reLevel = (i: number, delta: 1 | -1) => {
    const list = [...(items ?? [])];
    const row = list[i];
    if (!row) return;
    const prev = list[i - 1];
    // Indent needs a row above to nest under, and can only go one
    // deeper than it; outdent stops at the top level.
    const nextLevel =
      delta === 1
        ? Math.min(row.level + 1, (prev?.level ?? -1) + 1)
        : Math.max(0, row.level - 1);
    if (nextLevel === row.level) return;
    const shift = nextLevel - row.level;
    const len = subtreeLength(list, i);
    for (let k = i; k < i + len; k += 1) {
      list[k] = { ...list[k]!, level: Math.max(0, list[k]!.level + shift) };
    }
    mutate(list);
  };

  const commitRename = (i: number) => {
    const list = [...(items ?? [])];
    const row = list[i];
    setRenaming(null);
    if (!row) return;
    const title = draft.trim();
    if (!title || title === row.title) return;
    list[i] = { ...row, title };
    mutate(list);
  };

  const iconBtn =
    "inline-flex h-6 w-6 items-center justify-center rounded text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg)] disabled:opacity-30";

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col bg-[color:var(--color-bg)]" style={{ width: paneWidth }}>
      <div className="flex items-center gap-2 border-b border-[color:var(--color-border)] p-3">
        <i className="ri-bookmark-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
        <h2 className="text-sm font-semibold">Bookmarks</h2>
        <span className="text-xs text-[color:var(--color-fg-muted)]">{items?.length ?? "—"}</span>
        <button
          type="button"
          title={`Add a bookmark for page ${currentPage + 1}`}
          disabled={busy || !items}
          onClick={addHere}
          className="ml-auto inline-flex h-7 items-center gap-1 rounded-md border border-[color:var(--color-border)] px-2 text-xs hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
        >
          <i className="ri-add-line" aria-hidden /> Add
        </button>
        <button
          type="button"
          title="Close"
          aria-label="Close bookmarks"
          onClick={onClose}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
        >
          <i className="ri-close-line" aria-hidden />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {!items && (
          <p className="p-3 text-xs text-[color:var(--color-fg-muted)]">Reading outline…</p>
        )}
        {items?.length === 0 && (
          <p className="p-3 text-xs text-[color:var(--color-fg-muted)]">
            This document has no bookmarks. “Add” creates one for the page you’re on — they
            become a real PDF outline when you download.
          </p>
        )}
        {items?.map((item, i) => (
          <div
            key={`${i}:${item.title}`}
            className="group mb-0.5 flex items-center gap-1 rounded-md px-1 py-1 hover:bg-[color:var(--color-bg-subtle)]"
            style={{ paddingLeft: 4 + item.level * 14 }}
          >
            <i
              className={`${item.level === 0 ? "ri-bookmark-fill" : "ri-corner-down-right-line"} shrink-0 text-xs text-[color:var(--color-fg-muted)]`}
              aria-hidden
            />
            {renaming === i ? (
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => commitRename(i)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") commitRename(i);
                  if (e.key === "Escape") setRenaming(null);
                }}
                className="h-6 min-w-0 flex-1 rounded border border-[color:var(--color-accent)] bg-[color:var(--color-bg)] px-1 text-xs"
              />
            ) : (
              <button
                type="button"
                title={`Go to page ${item.page + 1}`}
                onClick={() => onJump(item.page)}
                onDoubleClick={() => {
                  setRenaming(i);
                  setDraft(item.title);
                }}
                className="min-w-0 flex-1 truncate text-left text-xs"
              >
                {item.title}
                <span className="ml-1 text-[10px] text-[color:var(--color-fg-muted)]">
                  p{item.page + 1}
                </span>
              </button>
            )}
            <span className="hidden shrink-0 group-hover:flex">
              <button type="button" title="Move up" disabled={busy || i === 0} onClick={() => move(i, -1)} className={iconBtn}>
                <i className="ri-arrow-up-s-line text-xs" aria-hidden />
              </button>
              <button type="button" title="Move down" disabled={busy || i === items.length - 1} onClick={() => move(i, 1)} className={iconBtn}>
                <i className="ri-arrow-down-s-line text-xs" aria-hidden />
              </button>
              <button type="button" title="Outdent" disabled={busy || item.level === 0} onClick={() => reLevel(i, -1)} className={iconBtn}>
                <i className="ri-arrow-left-s-line text-xs" aria-hidden />
              </button>
              <button
                type="button"
                title="Indent (nest under the row above)"
                disabled={busy || i === 0 || item.level > (items[i - 1]?.level ?? -1)}
                onClick={() => reLevel(i, 1)}
                className={iconBtn}
              >
                <i className="ri-arrow-right-s-line text-xs" aria-hidden />
              </button>
              <button
                type="button"
                title="Rename"
                disabled={busy}
                onClick={() => {
                  setRenaming(i);
                  setDraft(item.title);
                }}
                className={iconBtn}
              >
                <i className="ri-edit-line text-xs" aria-hidden />
              </button>
              <button
                type="button"
                title="Delete (with any nested bookmarks)"
                disabled={busy}
                onClick={() => {
                  const list = [...items];
                  list.splice(i, subtreeLength(list, i));
                  mutate(list);
                }}
                className={iconBtn}
              >
                <i className="ri-delete-bin-line text-xs" aria-hidden />
              </button>
            </span>
          </div>
        ))}
      </div>
      <p className="border-t border-[color:var(--color-border)] p-2 text-[10px] text-[color:var(--color-fg-muted)]">
        Double-click a bookmark to rename it. {pageCount} page{pageCount === 1 ? "" : "s"} —
        changes are undoable with Ctrl+Z.
      </p>
    </div>
  );
}
